import type { Feed, FeedItem } from "@knocklabs/client";
import Knock from "@knocklabs/client";
import { createEffect, createSignal, onCleanup } from "solid-js";

// Headless wrapper around @knocklabs/client's Feed - there's no prebuilt
// SolidJS component the way @knocklabs/react provides one for React (see
// tasks/notifications-plan.md Task 13). The feed_channel_id is workspace-
// specific and doesn't exist until Task 5 creates it in the Knock dashboard;
// until then, auth() resolves with null fields and this composable no-ops.

export type KnockFeedAuth = {
	publicKey: string | null;
	feedChannelId: string | null;
	userId: string;
	userToken?: string;
};

export function createKnockFeed(auth: () => KnockFeedAuth | undefined) {
	const [items, setItems] = createSignal<FeedItem[]>([]);
	const [unreadCount, setUnreadCount] = createSignal(0);
	const [loading, setLoading] = createSignal(true);
	const [feed, setFeed] = createSignal<Feed | undefined>();

	// Bumped when the page returns from the back/forward cache, so the effect
	// reconnects what it tore down on pagehide.
	const [restored, setRestored] = createSignal(0);
	const onPageShow = (event: PageTransitionEvent) => {
		if (event.persisted) {
			setRestored((n) => n + 1);
		}
	};
	window.addEventListener("pageshow", onPageShow);
	onCleanup(() => window.removeEventListener("pageshow", onPageShow));

	createEffect(() => {
		restored();
		const a = auth();
		if (!a?.publicKey || !a.feedChannelId) {
			return;
		}

		const knock = new Knock(a.publicKey);
		knock.authenticate(a.userId, a.userToken);
		const f = knock.feeds.initialize(a.feedChannelId);

		f.on("items.received.*", () => {
			const state = f.getState();
			setItems(state.items);
			setUnreadCount(state.metadata.unread_count);
			setLoading(false);
		});
		f.listenForUpdates();
		f.fetch();
		setFeed(f);

		const disconnect = () => {
			f.teardown();
			knock.teardown();
		};
		// Left open, the socket reconnects when the browser drops it during
		// unload, and that new socket lives until the page is finally gone.
		window.addEventListener("pagehide", disconnect);
		onCleanup(() => {
			window.removeEventListener("pagehide", disconnect);
			disconnect();
		});
	});

	return {
		items,
		unreadCount,
		loading,
		markAsRead: (item: FeedItem) => feed()?.markAsRead(item),
		markAllAsRead: () => feed()?.markAllAsRead(),
	};
}
